import React, { useState, useEffect, useRef } from "react";
import {
  Box, Button, Stack, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, IconButton, Alert, LinearProgress, Tooltip, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  ToggleButtonGroup, ToggleButton, Tabs, Tab,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import LanguageIcon from "@mui/icons-material/Language";
import AddIcon from "@mui/icons-material/Add";
import { botsApi } from "../../../api/bots.js";
import { EmptyState } from "../../../components/EmptyState.js";
import { BadgeChip, statusToBadgeColor } from "../../../components/BadgeChip.js";
import { GlowButton } from "../../../components/GlowButton.js";
import type { WebSource, WebSourceMode } from "@chatfab/shared";

// ── Shared helpers ─────────────────────────────────────────────────────────

function fmtBytes(b: number) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Document types ─────────────────────────────────────────────────────────

interface Doc {
  filename: string;
  size_bytes: number;
  last_modified: string;
  status: "pending" | "processing" | "complete" | "failed";
  chunk_count?: number;
  error_message?: string;
}

const DOC_LABEL: Record<string, string> = { pending: "Pending", processing: "Processing", complete: "Indexed", failed: "Failed" };
function DocStatusChip({ status }: { status: Doc["status"] }) {
  const spinning = status === "pending" || status === "processing";
  return <BadgeChip label={DOC_LABEL[status] ?? status} color={statusToBadgeColor(status)} spinning={spinning} />;
}

const ACCEPTED = ".pdf,.docx,.txt,.md";
const CONTENT_TYPES: Record<string, string> = {
  pdf:  "application/pdf",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  txt:  "text/plain",
  md:   "text/markdown",
};

function uploadWithProgress(url: string, file: File, ct: string, onProgress: (pct: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload  = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`));
    xhr.onerror = () => reject(new Error("Upload failed"));
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", ct);
    xhr.send(file);
  });
}

// ── Web source types ───────────────────────────────────────────────────────

const WEB_LABEL: Record<string, string> = { pending: "Pending", crawling: "Crawling", complete: "Indexed", failed: "Failed" };
function WebStatusChip({ status }: { status: WebSource["status"] }) {
  const spinning = status === "pending" || status === "crawling";
  return <BadgeChip label={WEB_LABEL[status] ?? status} color={statusToBadgeColor(status)} spinning={spinning} />;
}

// ── Add web source dialog ──────────────────────────────────────────────────

function AddWebSourceDialog({ open, onClose, onAdd }: {
  open: boolean;
  onClose: () => void;
  onAdd: (body: { name: string; mode: WebSourceMode; urls?: string[]; seed_url?: string; max_depth?: number; max_pages?: number }) => Promise<void>;
}) {
  const [mode, setMode] = useState<WebSourceMode>("url_list");
  const [name, setName] = useState("");
  const [urlsText, setUrlsText] = useState("");
  const [seedUrl, setSeedUrl] = useState("");
  const [maxDepth, setMaxDepth] = useState(1);
  const [maxPages, setMaxPages] = useState(50);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => { setName(""); setUrlsText(""); setSeedUrl(""); setMaxDepth(1); setMaxPages(50); setError(null); };

  const handleAdd = async () => {
    if (!name.trim()) { setError("Name is required"); return; }
    if (mode === "url_list" && !urlsText.trim()) { setError("Enter at least one URL"); return; }
    if (mode === "seed_crawl" && !seedUrl.trim()) { setError("Seed URL is required"); return; }

    setSaving(true);
    setError(null);
    try {
      const urls = mode === "url_list"
        ? urlsText.split("\n").map(u => u.trim()).filter(Boolean)
        : undefined;
      await onAdd({ name: name.trim(), mode, urls, seed_url: mode === "seed_crawl" ? seedUrl.trim() : undefined, max_depth: maxDepth, max_pages: maxPages });
      reset();
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to add web source");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add web source</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField label="Name" fullWidth required value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Company docs" />
          <Box>
            <Typography variant="body2" mb={1} color="text.secondary">Mode</Typography>
            <ToggleButtonGroup exclusive value={mode} onChange={(_, v) => { if (v) setMode(v); }} size="small">
              <ToggleButton value="url_list">URL list</ToggleButton>
              <ToggleButton value="seed_crawl">Crawl from seed</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {mode === "url_list" ? (
            <TextField
              label="URLs (one per line)" fullWidth multiline rows={5}
              value={urlsText} onChange={e => setUrlsText(e.target.value)}
              placeholder={"https://example.com/docs/page1\nhttps://example.com/docs/page2"}
            />
          ) : (
            <>
              <TextField label="Seed URL" fullWidth required value={seedUrl} onChange={e => setSeedUrl(e.target.value)} placeholder="https://example.com/docs" />
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Max depth" type="number" sx={{ width: 120 }}
                  value={maxDepth} onChange={e => setMaxDepth(Math.max(0, parseInt(e.target.value) || 0))}
                  helperText="Link hops from seed"
                  inputProps={{ min: 0, max: 3 }}
                />
                <TextField
                  label="Max pages" type="number" sx={{ width: 120 }}
                  value={maxPages} onChange={e => setMaxPages(Math.max(1, parseInt(e.target.value) || 1))}
                  helperText="Page limit"
                  inputProps={{ min: 1, max: 200 }}
                />
              </Stack>
            </>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={() => { reset(); onClose(); }}>Cancel</Button>
        <GlowButton onClick={handleAdd} loading={saving}>
          {saving ? "Adding…" : "Add source"}
        </GlowButton>
      </DialogActions>
    </Dialog>
  );
}

// ── Documents panel ────────────────────────────────────────────────────────

function DocumentsPanel({ botId, onUpdate }: { botId: string; onUpdate: () => void }) {
  const [docs, setDocs]           = useState<Doc[]>([]);
  const [totalChunks, setTotal]   = useState(0);
  const [loading, setLoading]     = useState(true);
  const [uploading, setUploading]     = useState(false);
  const [uploadPct, setUploadPct]     = useState(0);
  const [uploadingFile, setUploadingFile] = useState<string | null>(null);
  const [error, setError]             = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = () => {
    setLoading(true);
    botsApi.listDocuments(botId)
      .then(r => { setDocs(r.documents as Doc[]); setTotal(r.total_chunks); })
      .catch(() => setError("Failed to load documents"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [botId]);

  useEffect(() => {
    const needsPoll = docs.some(d => d.status === "pending" || d.status === "processing");
    if (!needsPoll) return;
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [docs]);

  const handleUpload = async (files: FileList | null) => {
    if (!files?.length) return;
    setError(null); setUploading(true); setUploadPct(0); setUploadingFile(null);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const ext  = file.name.split(".").pop()?.toLowerCase() ?? "";
        const ct   = CONTENT_TYPES[ext];
        if (!ct) { setError(`Unsupported file type: .${ext}`); continue; }
        setUploadingFile(file.name);
        setUploadPct(0);
        const { upload_url } = await botsApi.getUploadUrl(botId, file.name, ct, file.size);
        await uploadWithProgress(upload_url, file, ct, setUploadPct);
      }
      load(); onUpdate();
    } catch { setError("Upload failed — try again"); }
    finally { setUploading(false); setUploadPct(0); setUploadingFile(null); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="body2" color="text.secondary">{docs.length} file{docs.length !== 1 ? "s" : ""} · {totalChunks.toLocaleString()} chunks indexed</Typography>
        <Button variant="outlined" size="small" startIcon={<UploadFileIcon />} onClick={() => fileRef.current?.click()} disabled={uploading}>Upload</Button>
      </Stack>
      <input ref={fileRef} type="file" accept={ACCEPTED} multiple hidden onChange={e => handleUpload(e.target.files)} />
      {uploading && (
        <Box sx={{ mb: 2 }}>
          {uploadingFile && (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
              Uploading {uploadingFile}… {uploadPct}%
            </Typography>
          )}
          <LinearProgress variant="determinate" value={uploadPct} sx={{ borderRadius: 2 }} />
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {!loading && docs.length === 0 ? (
        <EmptyState
          icon={<InsertDriveFileOutlinedIcon />}
          title="No documents yet"
          description="Upload PDFs, Word docs, or text files."
          action={<GlowButton startIcon={<UploadFileIcon />} onClick={() => fileRef.current?.click()}>Upload your first document</GlowButton>}
        />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Filename</TableCell>
              <TableCell>Size</TableCell>
              <TableCell>Modified</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {docs.map(doc => (
              <TableRow key={doc.filename} hover>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <InsertDriveFileOutlinedIcon fontSize="small" sx={{ color: "text.disabled" }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2">{doc.filename}</Typography>
                      {doc.status === "complete" && doc.chunk_count !== undefined && (
                        <Typography variant="caption" color="text.secondary">{doc.chunk_count} chunks</Typography>
                      )}
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell><Typography variant="body2" color="text.secondary">{fmtBytes(doc.size_bytes)}</Typography></TableCell>
                <TableCell><Typography variant="body2" color="text.secondary">{new Date(doc.last_modified).toLocaleDateString()}</Typography></TableCell>
                <TableCell>
                  {doc.status === "failed" ? (
                    <Tooltip title="Indexing failed — click Re-index to try again"><span><DocStatusChip status={doc.status} /></span></Tooltip>
                  ) : (
                    <DocStatusChip status={doc.status} />
                  )}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Re-index">
                    <IconButton size="small" onClick={() => { botsApi.reingestDocument(botId, doc.filename).then(load); }}><RefreshIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => { botsApi.deleteDocument(botId, doc.filename).then(() => { load(); onUpdate(); }); }} sx={{ color: "error.main" }}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}

// ── Web sources panel ──────────────────────────────────────────────────────

function WebSourcesPanel({ botId }: { botId: string }) {
  const [sources, setSources] = useState<WebSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const load = () => {
    setLoading(true);
    botsApi.listWebSources(botId)
      .then(r => setSources(r.web_sources))
      .catch(() => setError("Failed to load web sources"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [botId]);

  useEffect(() => {
    const needsPoll = sources.some(s => s.status === "pending" || s.status === "crawling");
    if (!needsPoll) return;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [sources]);

  const handleAdd = async (body: Parameters<typeof botsApi.createWebSource>[1]) => {
    await botsApi.createWebSource(botId, body);
    load();
  };

  const handleDelete = async (id: string) => {
    await botsApi.deleteWebSource(botId, id);
    load();
  };

  const handleRecrawl = async (id: string) => {
    await botsApi.recrawlWebSource(botId, id);
    load();
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="body2" color="text.secondary">{sources.length} web source{sources.length !== 1 ? "s" : ""}</Typography>
        <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>Add source</Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {!loading && sources.length === 0 ? (
        <EmptyState
          icon={<LanguageIcon />}
          title="No web sources yet"
          description="Add URLs or crawl a site to let your bot answer from live web content."
          action={<GlowButton startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>Add your first web source</GlowButton>}
        />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Mode</TableCell>
              <TableCell>Pages</TableCell>
              <TableCell>Chunks</TableCell>
              <TableCell>Last crawled</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sources.map(s => (
              <TableRow key={s.websource_id} hover>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <LanguageIcon fontSize="small" sx={{ color: "text.disabled" }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2">{s.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {s.mode === "url_list" ? `${s.urls?.length ?? 0} URL${(s.urls?.length ?? 0) !== 1 ? "s" : ""}` : s.seed_url}
                      </Typography>
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell><BadgeChip label={s.mode === "url_list" ? "URL list" : "Crawl"} color="purple" /></TableCell>
                <TableCell><Typography variant="body2" color="text.secondary">{s.page_count}</Typography></TableCell>
                <TableCell><Typography variant="body2" color="text.secondary">{s.chunk_count.toLocaleString()}</Typography></TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {s.last_crawled_at ? new Date(s.last_crawled_at).toLocaleDateString() : "—"}
                  </Typography>
                </TableCell>
                <TableCell>
                  {s.status === "failed" ? (
                    <Tooltip title="Crawl failed — click Recrawl to try again"><span><WebStatusChip status={s.status} /></span></Tooltip>
                  ) : (
                    <WebStatusChip status={s.status} />
                  )}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Recrawl">
                    <IconButton size="small" onClick={() => handleRecrawl(s.websource_id)} disabled={s.status === "crawling" || s.status === "pending"}>
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => handleDelete(s.websource_id)} sx={{ color: "error.main" }}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <AddWebSourceDialog open={addOpen} onClose={() => setAddOpen(false)} onAdd={handleAdd} />
    </Box>
  );
}

// ── Main tab ───────────────────────────────────────────────────────────────

export function DataSourcesTab({ botId, onUpdate }: { botId: string; onUpdate: () => void }) {
  const [panel, setPanel] = useState(0);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h4">Data Sources</Typography>
      </Stack>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs value={panel} onChange={(_, v) => setPanel(v)}>
          <Tab label="Documents" icon={<InsertDriveFileOutlinedIcon fontSize="small" />} iconPosition="start" />
          <Tab label="Web sources" icon={<LanguageIcon fontSize="small" />} iconPosition="start" />
        </Tabs>
      </Box>

      {panel === 0 && <DocumentsPanel botId={botId} onUpdate={onUpdate} />}
      {panel === 1 && <WebSourcesPanel botId={botId} />}
    </Box>
  );
}
